import { UserInfo } from 'types/UserInfo';

// The signed-in player. Kept for this tab only (sessionStorage), or, when
// the player keeps "Stay signed in" (the default), across closing the app
// (localStorage): an iPhone home-screen app clears sessionStorage each time
// it is closed, and players had to type their password every time.
const USER_KEY = 'loggedUser';
const REMEMBER_KEY = 'tottalo.rememberLogin';

/** Whether the next sign-in stays after the app is closed (default yes). */
export function readRememberLogin(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) !== '0';
  } catch {
    return true;
  }
}

export function saveRememberLogin(remember: boolean) {
  try {
    localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
  } catch {
    // private mode: the choice lasts this visit only
  }
}

export function readStoredUser(): UserInfo | null {
  try {
    const raw =
      sessionStorage.getItem(USER_KEY) ?? localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Stores the player: a new sign-in where the player chose, an update (new
 * balance, new tokens) where the sign-in already is.
 */
export function writeStoredUser(user: UserInfo, newLogin = false) {
  try {
    const remembered = newLogin
      ? readRememberLogin()
      : sessionStorage.getItem(USER_KEY) == null &&
        localStorage.getItem(USER_KEY) != null;
    const raw = JSON.stringify(user);
    if (remembered) {
      localStorage.setItem(USER_KEY, raw);
      sessionStorage.removeItem(USER_KEY);
    } else {
      sessionStorage.setItem(USER_KEY, raw);
      localStorage.removeItem(USER_KEY);
    }
  } catch {
    // storage blocked: the player stays signed in until the page closes
  }
}

export function clearStoredUser() {
  try {
    sessionStorage.removeItem(USER_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // nothing stored
  }
}

export function isAuthenticated(): boolean {
  return readStoredUser() != null;
}

export function setLoginGatePending(pending: boolean) {
  document.documentElement.classList.toggle('login-gate-pending', pending);
}

export function syncLoginGatePending() {
  setLoginGatePending(!isAuthenticated());
}
